import { Controller } from "@hotwired/stimulus"

// Light/dark appearance, persisted per-device in localStorage ("theme":
// light | dark | system). A FOUC-prevention script in the <head> applies the
// stored choice before paint; this controller handles user toggles, keeps the
// nav moon/sun glyph and the settings option buttons in sync, and follows the
// OS when the preference is "system".
const KEY = "theme"
const ACTIVE = [ "bg-white", "text-primary-700", "shadow-card" ]
const INACTIVE = [ "text-gray-500" ]

export default class extends Controller {
  static targets = ["moon", "sun", "option"]

  connect() {
    this.media = window.matchMedia("(prefers-color-scheme: dark)")
    this.onMedia = () => { if (this.stored === "system") this.apply() }
    this.media.addEventListener("change", this.onMedia)
    // Keep multiple instances (nav button + settings card) in lockstep.
    this.onChanged = () => this.sync()
    window.addEventListener("theme:changed", this.onChanged)
    this.sync()
  }

  disconnect() {
    if (this.media) this.media.removeEventListener("change", this.onMedia)
    window.removeEventListener("theme:changed", this.onChanged)
  }

  get stored() {
    try { return localStorage.getItem(KEY) || "system" } catch (e) { return "system" }
  }

  set stored(value) {
    try { localStorage.setItem(KEY, value) } catch (e) { /* private mode */ }
  }

  get effectiveDark() {
    const t = this.stored
    return t === "dark" || (t === "system" && this.media.matches)
  }

  // Nav button: flip to the opposite explicit theme.
  toggle() {
    this.stored = this.effectiveDark ? "light" : "dark"
    this.apply()
  }

  // Settings option button: set an explicit preference (light/dark/system).
  choose(event) {
    const value = event.currentTarget.dataset.themeValue
    if ([ "light", "dark", "system" ].includes(value)) {
      this.stored = value
      this.apply()
    }
  }

  apply() {
    document.documentElement.classList.toggle("dark", this.effectiveDark)
    this.sync()
    window.dispatchEvent(new CustomEvent("theme:changed"))
  }

  sync() {
    const dark = this.effectiveDark
    this.moonTargets.forEach((el) => el.classList.toggle("hidden", dark))
    this.sunTargets.forEach((el) => el.classList.toggle("hidden", !dark))
    this.optionTargets.forEach((el) => {
      const active = el.dataset.themeValue === this.stored
      el.classList.toggle("bg-white", active)
      el.classList.toggle("text-primary-700", active)
      el.classList.toggle("shadow-card", active)
      el.classList.toggle("text-gray-500", !active)
    })
  }
}
